
// src/scanresult/Summary.js
import React, { useMemo } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button, Box, BarChart, Link
} from '@barracuda-internal/bds-core';
import { BotAttackIcon, DDoSAttackIcon, SQLInjectionIcon, AttackIcon } from "../utils/icon";
import { SmallSeverityBars } from '../utils/utility';
import { getSeverityText } from '../utils/color';
import { Divider } from "@mui/material";

import { getWebServerIcon } from '../icons/WebServerIcons';
import { getLanguageIcon } from '../icons/LanguageIcons';
import InsightsIcon from '@mui/icons-material/Insights';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';

import AiInsightsDrawer from '../utils/AiInsightsDrawer';
import DrawerDetails from '../utils/drawer';
import AmGauge from './AmGauge';
import Tooltip from "@mui/material/Tooltip";


// const GREEN = 'rgba(1, 224, 16, 1)';
// const GREEN_FADE = 'rgba(1, 224, 16, 0.2)';
// const RED = 'rgba(211, 47, 47, 1)';
// const RED_FADE = 'rgba(211, 47, 47, 0.2)';
// const YELLOW = 'rgba(255, 235, 59, 1)';
// const YELLOW_FADE = 'rgba(255, 235, 59, 0.2)';
// const ORANGE = 'rgba(255, 152, 0, 1)';
// const ORANGE_FADE = 'rgba(255, 152, 0, 0.2)';


const GREEN = '#2e7d32';
const GREEN_FADE = '#2e7d32';
const RED = '#d32f2f';
const RED_FADE = '#d32f2f';
const YELLOW = '#f9a825';
const YELLOW_FADE = '#f9a825';
const ORANGE = '#fb8c00';
const ORANGE_FADE = '#fb8c00';

function getScoreColor(score) {
  if (typeof score !== "number") return "#d32f2f";
  if (score >= 90) return GREEN; // green
  if (score >= 80) return YELLOW; // yellow
  if (score >= 60) return ORANGE; // orange
  return RED; // red
}

export default function Summary({ ai_summary, summary, framework_summary, vulnerabilities, waf_detected, scrollPage, sub_domains_count }) {
  // Backend payload expected at: state.scanResult.data
  const data = summary || {};
  const [aiInsightsOpen, setAiInsightsOpen] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);

  // If backend not integrated yet (empty data), fall back to mock data so the UI shows example content.
  const hasBackendData = data && Object.keys(data).length > 0;

  const mockData = useMemo(() => ({
    security_posture_score: 90,
    target_domain: 'demo.com',
    scan_completed_at: 'Oct 1, 2025 8:00 AM',
    vulnerabilities: [
      { title: 'SQL injection', severity: 'high', description: 'Hackers can steal, change, or delete data, and in some cases take control of the backend database.', instances: 1 },
      { title: 'Content Security Policy header not set', severity: 'medium', description: 'CSP header missing', instances: 12 },
      { title: 'Cross-domain misconfiguration', severity: 'medium', description: '', instances: 7 },
      { title: 'Missing Anti-clickjacking header', severity: 'medium', description: '', instances: 3 },
      { title: 'Session ID in URL rewrite', severity: 'medium', description: '', instances: 2 },
      { title: 'Vulnerable JS library', severity: 'low', description: '', instances: 1 }
    ]
  }), []);

  const effectiveData = hasBackendData ? data : mockData;

  const score =
    typeof effectiveData.security_posture_score === "number"
      ? effectiveData.security_posture_score
      : typeof effectiveData.score === "number"
        ? effectiveData.score
        : null;

  const targetDomain = effectiveData.target_domain || effectiveData.domain || effectiveData.host || "";
  const completedOn = effectiveData.scan_completed_at || effectiveData.scanned_at || effectiveData.scan_completed_at || "";
  const vulnerabilities_detected = effectiveData?.vulnerabilities_detected || null

  const scoreColor = getScoreColor(score ?? -1);
  // compute proportional bar width based on score (0-100)
  const maxBarWidth = 300; // px for score == 100
  const safeScore = typeof score === 'number' && !Number.isNaN(score) ? Math.max(0, Math.min(100, score)) : 0;
  const barWidth = Math.round((safeScore / 100) * maxBarWidth);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Row: Vulnerabilities by severity (left) and Top risks (right) */}
      <Grid container spacing={1}>
        {/* Left summary Cards */}
        <Grid container spacing={1} item xs={12} md={3} >
          <Grid item xs={12}>
            <Paper sx={{ p: 2, minHeight: 130, display: 'flex', flexDirection: 'column', }}>
              <Grid container alignItems="center" spacing={1}>
                <Grid xs={11} item>
                  <Typography variant="subtitle1" sx={{ fontWeight: 560 }} >
                    Overall Application Security Health
                  </Typography>
                </Grid>
                <Grid xs={1} item>
                  <Typography variant="subtitle1" sx={{ fontWeight: 560 }} >

                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3.5 }}>
                <AmGauge value={safeScore} width={280} height={150} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <Typography variant="h3" sx={{ fontWeight: 600, lineHeight: 1 }}>
                    {(() => {
                      if (typeof score === "number") {
                        
                        if (score >= 100) return "Excellent";
                        if (score >= 75) return "Good";
                        if (score >= 50) return "Average";
                        if (score >= 25) return "Bad";
                        return "Poor";
                      }
                      return "-";
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  {/* <Button bdsType={'interactiveSubtle'} onClick={() => scrollPage(4)}>
                    Why is it {(() => {
                      if (typeof score === "number") {
                        if (score >= 100) return "Excellent";
                        if (score >= 75) return "Good";
                        if (score >= 50) return "Average";
                        if (score >= 25) return "Bad";
                        return "Poor";
                      }
                      return "?";
                    })()}?
                  </Button> */}
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, minHeight: 130 }}>
              <Grid container alignItems="center" spacing={1}>
                <Grid xs={11} item>
                  <Typography variant="subtitle1" sx={{ fontWeight: 560 }} >
                    Non-Compliance Risks
                  </Typography>
                </Grid>
                <Grid xs={1} item>
                  <Typography variant="subtitle1" sx={{ fontWeight: 560 }} >

                  </Typography>
                </Grid>
                <Grid md={3} container item spacing={2} alignContent="center" justifyContent="center" direction="column" alignItems="center">
                  <Grid item md={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Button 
                      bdsType='interactiveSubtle'
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollPage(1, 'soc_2', summary?.compliance_scanner_map?.soc_2 || []);
                      }}
                      sx={{ p: 0, minWidth: 'auto', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                        {summary?.compliance_score?.soc_2 || 0}
                        {/* { console.log(summary?.compliance_scanner_map)} */}
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item md={12} sx={{ textAlign: 'center' }}>SOC 2</Grid>
                </Grid>
                <Grid md={3} container item spacing={2} alignContent="center" justifyContent="center" direction="column" alignItems="center">
                  <Grid item md={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Button 
                      bdsType='interactiveSubtle'
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollPage(1, 'gdpr', summary?.compliance_scanner_map?.gdpr || []);
                      }}
                      sx={{ p: 0, minWidth: 'auto', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                        {summary?.compliance_score?.gdpr || 0}
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item md={12} sx={{ textAlign: 'center' }}>GDPR</Grid>
                </Grid>
                <Grid md={3} container item spacing={2} alignContent="center" justifyContent="center" direction="column" alignItems="center">
                  <Grid item md={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Button 
                      bdsType='interactiveSubtle'
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollPage(1, 'pci_dss', summary?.compliance_scanner_map?.pci_dss || []);
                      }}
                      sx={{ p: 0, minWidth: 'auto', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                        {summary?.compliance_score?.pci_dss || 0}
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item md={12} sx={{ textAlign: 'center' }}>PCI DSS</Grid>
                </Grid>
                <Grid md={3} container item spacing={2} alignContent="center" justifyContent="center" direction="column" alignItems="center">
                  <Grid item md={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Button 
                      bdsType='interactiveSubtle'
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollPage(1, 'hipaa', summary?.compliance_scanner_map?.hipaa || []);
                      }}
                      sx={{ p: 0, minWidth: 'auto', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                        {summary?.compliance_score?.hipaa || 0}
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item md={12} sx={{ textAlign: 'center' }}>HIPAA</Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

         
        </Grid>
        {/* Right main Cards- chart and top risks and WAF detection*/}
        <Grid container spacing={1} item xs={12} md={9} style={{ height: '100%' }}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' , height: '100%'}}>
              <Grid
                item
                md={12}
                display="flex"
                flexDirection="column"
                 >
                <Typography variant="subtitle1" sx={{ fontWeight: 560 }}>
                  Total Vulnerabilities
                </Typography>
                <Typography variant="h1" color="text.primary" sx={{ fontWeight: 500, mt: 0.9, ml: 3 }}>
                  {vulnerabilities_detected !== null ? vulnerabilities_detected : '-'}
                </Typography>

              </Grid>


              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 560 }}>Vulnerabilities by severity </Typography>
                <Button bdsType={'interactiveSubtle'} onClick={() => scrollPage(1)}>View all ↓</Button>
              </Box>
              <Grid container sx={{ mt: 1, textAlign: 'center' }}>
                <BarChart
                  chartData={{
                    datasets: [
                      {
                        label: 'Total Vulnerabilities:' + (vulnerabilities_detected !== null ? ` ${vulnerabilities_detected}` : ''),
                        data: [
                          (effectiveData.vulnerabilities_by_severity?.[4] ?? 0), // high
                          (effectiveData.vulnerabilities_by_severity?.[3] ?? 0), // medium
                          (effectiveData.vulnerabilities_by_severity?.[2] ?? 0), // low
                          (effectiveData.vulnerabilities_by_severity?.[1] ?? 0), // info
                        ],
                        backgroundColor: [
                                                    '#1976d2',
                                                    '#1e88e5',
                                                    '#64b5f6',
                                                    '#90caf9'
                        ], // Bar fill colors (design tokens)
                        borderColor: [
                                                    '#1976d2',
                                                    '#1e88e5',
                                                    '#64b5f6',
                                                    '#90caf9'
                        ], // Bar border colors (design tokens)
                        borderWidth: .1,
                        barThickness: 40,
                      }
                    ], labels: ['High', 'Medium', 'Low', 'Info'],
                  }}
                  chartDimension={{ height: '230px', width: '100%' }}
                  emptyState={{
                    icon: <div data-testid="empty-state" />,
                    text: 'Too full to eat anymore twix'
                  }}
                  hasEntries={true}
                  showFooter={false}
                  showLegend={false}
                  barChartOptions={{
                    plugins: { tooltip: { enabled: false } }, scales: {
                      x: {
                        grid: {
                          display: false, // Removes vertical grid lines
                          drawOnChartArea: false // Removes grid lines only within the chart area
                        }
                      },
                      y: {
                        grid: {
                          display: false, // Removes horizontal grid lines
                          drawOnChartArea: false // Removes grid lines only within the chart area
                        }, border: {
                          display: false // This hides the X-axis line
                        }, ticks: {
                          display: false // This hides the Y-axis labels
                        }
                      }
                    }
                  }}
                />
              </Grid>

              {/* <Divider sx={{ my: 1 }} /> */}

              <Grid container sx={{ mt: 0.5, ml: 4 }}>


                <Grid>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginRight: '40px',
                    marginTop: '10px',
                    background: '#1976d2',
                    color: 'white'
                  }} >High: {summary?.vulnerabilities_by_severity?.[4]}</span>
                </Grid>
                <Grid >
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginRight: '40px',
                    marginTop: '10px',
                    background: '#1e88e5',
                    color: 'white'
                  }} >Medium: {summary?.vulnerabilities_by_severity?.[3]}</span>
                </Grid>
                <Grid >
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginRight: '40px',
                    marginTop: '10px',
                    background: '#64b5f6',
                    color: 'white'
                  }} >Low: {summary?.vulnerabilities_by_severity?.[2]}</span>
                </Grid>
                <Grid >
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    marginRight: '40px',
                    marginTop: '10px',
                    background: '#90caf9',
                    color: 'white'
                  }} >Info: {summary?.vulnerabilities_by_severity?.[1]}</span>
                </Grid>
              </Grid>
              
            </Paper>
          </Grid>
          <Grid item xs={12} md={7} >
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 560 }}>Top 5 risks </Typography>
                <Button bdsType={'interactiveSubtle'} onClick={() => scrollPage(1)}>View all ↓</Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
                {vulnerabilities.slice(0, 5).map((t, i) => (
                  <Grid container key={i} spacing={0} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.6 }}>
                   <Grid item md={4}>
  <Typography 
    variant="subtitle1"
    noWrap
  >
    {t.attack_type_title || t.name}
  </Typography>
</Grid>

                    <Grid item md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5}}>
                      <Typography variant="body2" color="text.secondary">Severity: {getSeverityText(t.severity_level)}</Typography>
                      <SmallSeverityBars severity={t.severity_level} />
                    </Grid>
                    <Grid item md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        bdsType='interactiveSubtle'
                        onClick={() => {
                          setSelected(t);
                          setDrawerOpen(true);
                        }}
                      >
                        View details
                      </Button>
                    </Grid>
                    <Grid item md={12} ><Divider /></Grid>
                  </Grid>
                ))}
              </Box>
              {/* show first/top risk detail below the list */}
              {vulnerabilities && vulnerabilities[0] ? (
                <Box sx={{ mt: 1 }} style={{ justifyContent: 'flex-start' }}>
                  <Typography sx={{ fontWeight: 700 }}>{vulnerabilities[0].attack_type_title || vulnerabilities[0].name}</Typography>
                  {vulnerabilities[0].description ? (
                    <Box sx={{ maxHeight: '40px', overflowY: 'auto', mt: 0.5, pr: 1 }}>
                      <Typography variant="body2" color="text.secondary">{vulnerabilities[0].description}</Typography>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Paper>
          </Grid>
          
          {/* <Grid item xs={12} md={12}>
            {waf_detected?.detected !== true ?
              <Paper sx={{ minHeight: 150, p: 1, backgroundColor: '#fafafa' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Attack exposure</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>There is <b>no Web Application Firewall</b> detected on your domain and your web application is vulnerable to a wide range of common cyberattacks.</Typography>

                <Grid container spacing={0}>
                  <Grid item xs={2} md={4} >
                    <Typography sx={{ fontWeight: 700, color: '#111' }}> Bot attack</Typography>
                  </Grid>
                  <Grid item xs={2} md={4} >
                    <Typography sx={{ fontWeight: 700, color: '#111' }}> DDoS attack</Typography>
                  </Grid>
                  <Grid item xs={2} md={4} >
                    <Typography sx={{ fontWeight: 700, color: '#111' }}> SQL injection</Typography>
                  </Grid>
                </Grid>
              </Paper>
              :
              <Paper sx={{ minHeight: 150, p: 1, backgroundColor: '#fafafa' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>WAF Detected</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>A Web Application Firewall (WAF) has been detected on your domain. This helps protect your web application from common cyberattacks.</Typography>
                <Box sx={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'nowrap', mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: '#070606ff', whiteSpace: 'nowrap' }}>WAF Name:</Typography>
                    <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{waf_detected?.firewall || "Unknown WAF"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: '#111', whiteSpace: 'nowrap' }}>Manufacturer:</Typography>
                    <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{waf_detected?.manufacturer || "Unknown Manufacturer"}</Typography>
                  </Box>
                </Box>
              </Paper>
            }
          </Grid> */}
          
        </Grid>
      
      </Grid>

      {/* --- Dynamic Attack Surface & WAF Section --- */}
      {(() => {
        // Determine which Attack Surface items are present (excluding Sub Domains)
        const attackSurfaceItems = [
          framework_summary?.web_server?.display_name,
          framework_summary?.programming_languages?.display_name,
          framework_summary?.web_framework?.display_name
        ].filter(Boolean);
        const onlySubdomain =
          !attackSurfaceItems.length && sub_domains_count !== undefined;
        // Set md values based on presence
        const attackSurfaceMd = onlySubdomain ? 3.5 : 5.4;
        const wafMd = onlySubdomain ? 8.5 : 6.6;
        // Build Attack Surface items array for rendering
        const attackSurfaceRenderItems = [
          framework_summary?.web_server?.display_name && {
            label: "Web Server",
            value: framework_summary.web_server.display_name,
          },
          framework_summary?.programming_languages?.display_name && {
            label: "Programming Language",
            value: framework_summary.programming_languages.display_name,
          },
          framework_summary?.web_framework?.display_name && {
            label: "Web Framework",
            value: framework_summary.web_framework.display_name,
          },
          sub_domains_count !== undefined && {
            label: "Sub Domains",
            value: sub_domains_count,
            clickable: true,
          },
        ].filter(Boolean).slice(0, 4);
        return (
          <Grid container spacing={1}>
            {/* Attack Surface */}
            <Grid item xs={12} md={attackSurfaceMd}>
              <Paper sx={{ p: 2, minHeight: 150 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 560, mb: 1 }}>
                  Attack Surface
                </Typography>
                <Grid container spacing={1}>
                  {attackSurfaceRenderItems.map((item, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {item.label}:
                        </Typography>
                        <Button
                          bdsType="interactiveSubtle"
                          sx={{ p: 1 }}
                          onClick={item.clickable ? () => scrollPage(3) : undefined}
                        >
                          {item.value}
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
            {/* WAF Section */}
            <Grid item xs={12} md={wafMd}>
              {waf_detected?.detected !== true ? (
                <Paper sx={{ minHeight: 150, p: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Attack exposure</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>There is <b>no Web Application Firewall</b> detected on your domain and your web application is vulnerable to a wide range of common cyberattacks.</Typography>
                  <Grid container spacing={0}>
                    <Grid item xs={2} md={4} >
                      <Typography sx={{ fontWeight: 700, color: '#111' }}> Bot attack</Typography>
                    </Grid>
                    <Grid item xs={2} md={4} >
                      <Typography sx={{ fontWeight: 700, color: '#111' }}> DDoS attack</Typography>
                    </Grid>
                    <Grid item xs={2} md={4} >
                      <Typography sx={{ fontWeight: 700, color: '#111' }}> SQL injection</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ) : (
                <Paper sx={{ minHeight: 150, p: 1}}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>WAF Detected</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>A Web Application Firewall (WAF) has been detected on your domain. This helps protect your web application from common cyberattacks.</Typography>
                  <Box sx={{ display: 'flex', gap: '40px', alignItems: 'center', flexWrap: 'nowrap', mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: '#070606ff', whiteSpace: 'nowrap' }}>WAF Name:</Typography>
                      <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{waf_detected?.firewall || "Unknown WAF"}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: '#111', whiteSpace: 'nowrap' }}>Manufacturer:</Typography>
                      <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{waf_detected?.manufacturer || "Unknown Manufacturer"}</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        );
      })()}

      {/* <AiInsightsDrawer open={aiInsightsOpen} onClose={() => setAiInsightsOpen(false)} ai_summary={ai_summary} /> */}
      <DrawerDetails
        url={'url'}
        open={drawerOpen}
        selected={selected}
        onClose={() => { setDrawerOpen(false); setSelected(null); }}
        isSafe={false} />
    </Box>
  );
}
